type Separador = '.' | ',';

const GRUPOS_DE_MILHAR: Record<Separador, RegExp> = {
  '.': /^\d{1,3}(?:\.\d{3})+$/,
  ',': /^\d{1,3}(?:,\d{3})+$/,
};

const ocorrencias = (texto: string, separador: Separador) => texto.split(separador).length - 1;

/**
 * Com os dois separadores, o último é o decimal. Com um só, ele é o decimal se aparece uma vez,
 * e o de milhar se se repete.
 *
 * Um separador sozinho, uma vez só, é sempre o decimal — inclusive o ponto, para que quem já
 * digitava "1000.00" continue lendo o mesmo número. Por isso "1.234" segue valendo 1,234, como
 * valia com o `Number()`.
 */
function separadorDecimal(corpo: string): Separador | null {
  const virgulas = ocorrencias(corpo, ',');
  const pontos = ocorrencias(corpo, '.');

  if (virgulas > 0 && pontos > 0) {
    return corpo.lastIndexOf(',') > corpo.lastIndexOf('.') ? ',' : '.';
  }
  if (virgulas === 1) return ',';
  if (pontos === 1) return '.';
  return null;
}

function semSeparadorDeMilhar(inteiro: string): string | null {
  if (/^\d*$/.test(inteiro)) return inteiro;

  const separador = (['.', ','] as const).find(candidato =>
    GRUPOS_DE_MILHAR[candidato].test(inteiro)
  );
  return separador ? inteiro.split(separador).join('') : null;
}

/**
 * Lê o número digitado num campo de valor, do jeito que o brasileiro digita ("1.234,56") ou com
 * ponto decimal ("1234.56").
 *
 * Campo vazio vale zero. Texto que não é número devolve `null`, e não zero: o zero em silêncio
 * manda adiante um valor que ninguém digitou, e quem chama é que decide como avisar.
 *
 * O espaço só é ignorado nas pontas. No meio, ele não é apagado: apagá-lo juntaria dígitos que
 * ninguém juntou ("10 5" viraria 105, e "1000 50", 100050), e o texto devolve `null`.
 */
export function lerNumeroDigitado(texto: string): number | null {
  const limpo = texto.trim();
  if (limpo === '') return 0;

  const corpo = limpo.replace(/^[-+]/, '');
  if (!/^[\d.,]+$/.test(corpo) || !/\d/.test(corpo)) return null;

  const decimal = separadorDecimal(corpo);
  const posicaoDecimal = decimal ? corpo.lastIndexOf(decimal) : corpo.length;
  const inteiro = semSeparadorDeMilhar(corpo.slice(0, posicaoDecimal));
  if (inteiro === null) return null;

  const fracao = corpo.slice(posicaoDecimal + 1);
  const valor = Number(`${inteiro || '0'}.${fracao || '0'}`);
  return limpo.startsWith('-') ? -valor : valor;
}
